import { useRef, useEffect, useState, useId } from 'react';
import Markdown from '../markdown.jsx';

/**
 * A textarea with formatting.
 *
 * A textarea rather than a contenteditable surface, deliberately. The stored value has to stay
 * markdown — it is pasted into prompts and read as plain text by everything downstream — and a
 * contenteditable would mean owning a serialiser, a paste sanitiser and a caret model to arrive
 * back at the text somebody could have typed. Here the buttons and the keys are shortcuts for
 * characters, the field is always the truth, and Preview shows what it will look like.
 *
 * Which means the field degrades correctly: someone who ignores the toolbar entirely and types
 * plain prose gets plain prose, and someone who already writes markdown finds it works.
 */

const MARKS = [
  { id: 'bold',   label: 'B',  title: 'Bold  Ctrl+B',        wrap: '**', style: { fontWeight: 700 } },
  { id: 'italic', label: 'I',  title: 'Italic  Ctrl+I',      wrap: '_',  style: { fontStyle: 'italic' } },
  { id: 'code',   label: '<>', title: 'Inline code',         wrap: '`',  mono: true },
];

const PREFIXES = [
  { id: 'h',      label: 'H',  title: 'Heading',         prefix: '## ' },
  { id: 'ul',     label: '•',  title: 'Bulleted list',   prefix: '- ' },
  { id: 'ol',     label: '1.', title: 'Numbered list',   prefix: '1. ', numbered: true },
  { id: 'quote',  label: '❝',  title: 'Quote',           prefix: '> ' },
];

/** The line boundaries containing [start, end). */
function lineSpan(value, start, end) {
  const from = value.lastIndexOf('\n', start - 1) + 1;
  const to = value.indexOf('\n', end);
  return [from, to === -1 ? value.length : to];
}

export default function MarkdownEditor({
  id, value, onChange, placeholder, height, minHeight = 104, disabled = false,
}) {
  const ta = useRef(null);
  // A selection to restore after the controlled value has been re-rendered. Set during an edit,
  // applied in the effect below — setting it before React writes the new value would put the
  // caret at a position in the old string.
  const nextSel = useRef(null);
  const [tab, setTab] = useState('write');
  const fallbackId = useId();
  const fieldId = id || fallbackId;

  useEffect(() => {
    if (!nextSel.current || !ta.current) return;
    const [from, to] = nextSel.current;
    nextSel.current = null;
    ta.current.focus();
    ta.current.setSelectionRange(from, to);
  });

  /** Apply an edit expressed as (value, selectionStart, selectionEnd) → [text, from, to]. */
  const edit = (fn) => {
    const el = ta.current;
    if (!el || disabled) return;
    const [text, from, to] = fn(el.value, el.selectionStart, el.selectionEnd);
    nextSel.current = [from, to];
    onChange(text);
  };

  const toggleWrap = (mark) => edit((text, start, end) => {
    const before = text.slice(0, start);
    const selected = text.slice(start, end);
    const after = text.slice(end);
    const w = mark.length;

    // Already wrapped — unwrap, so the button is a toggle rather than a way to end up with
    // ****four asterisks**** around a word.
    if (selected.startsWith(mark) && selected.endsWith(mark) && selected.length > w * 2) {
      const inner = selected.slice(w, -w);
      return [before + inner + after, start, start + inner.length];
    }
    if (before.endsWith(mark) && after.startsWith(mark)) {
      return [before.slice(0, -w) + selected + after.slice(w), start - w, end - w];
    }
    if (!selected) {
      return [`${before}${mark}${mark}${after}`, start + w, start + w];
    }
    return [`${before}${mark}${selected}${mark}${after}`, start + w, end + w];
  });

  const togglePrefix = (item) => edit((text, start, end) => {
    const [from, to] = lineSpan(text, start, end);
    const lines = text.slice(from, to).split('\n');
    const has = lines.every(l => !l.trim() || l.startsWith(item.prefix)
      || (item.numbered && /^\d+[.)]\s/.test(l)));

    const next = lines.map((line, n) => {
      if (has) {
        return line.replace(item.numbered ? /^\d+[.)]\s/ : item.prefix, '');
      }
      const bare = line.replace(/^(#{1,4}\s|[-*+]\s|\d+[.)]\s|>\s?)/, '');
      return (item.numbered ? `${n + 1}. ` : item.prefix) + bare;
    }).join('\n');

    return [text.slice(0, from) + next + text.slice(to), from, from + next.length];
  });

  const insertLink = () => edit((text, start, end) => {
    const selected = text.slice(start, end);
    const looksLikeUrl = /^(https?:\/\/|mailto:|www\.)/i.test(selected.trim());
    const label = looksLikeUrl ? 'link text' : (selected || 'link text');
    const href = looksLikeUrl ? selected.trim() : 'https://';
    const md = `[${label}](${href})`;
    // Land the caret on the half that still needs typing.
    const from = start + (looksLikeUrl ? 1 : md.length - href.length - 1);
    const to = from + (looksLikeUrl ? label.length : href.length);
    return [text.slice(0, start) + md + text.slice(end), from, to];
  });

  const onKeyDown = (e) => {
    const meta = e.ctrlKey || e.metaKey;
    if (meta && !e.altKey) {
      const key = e.key.toLowerCase();
      if (key === 'b') { e.preventDefault(); return toggleWrap('**'); }
      if (key === 'i') { e.preventDefault(); return toggleWrap('_'); }
      if (key === 'k') { e.preventDefault(); return insertLink(); }
    }
    // Enter continues the list you are in — and an empty item ends it, which is the only way
    // out that does not involve deleting the bullet the editor just wrote for you.
    if (e.key === 'Enter' && !e.shiftKey && !meta) {
      const el = e.currentTarget;
      if (el.selectionStart !== el.selectionEnd) return undefined;
      const from = el.value.lastIndexOf('\n', el.selectionStart - 1) + 1;
      const line = el.value.slice(from, el.selectionStart);
      const m = line.match(/^(\s*)([-*+]|\d+[.)])\s+(\[[ xX]\]\s+)?/);
      if (!m) return undefined;
      e.preventDefault();
      if (line.length === m[0].length) {
        return edit((text, start) => [text.slice(0, from) + text.slice(start), from, from]);
      }
      const bullet = /^\d/.test(m[2]) ? `${parseInt(m[2], 10) + 1}. ` : `${m[2]} `;
      const box = m[3] ? '[ ] ' : '';
      const insert = `\n${m[1]}${bullet}${box}`;
      return edit((text, start) => [
        text.slice(0, start) + insert + text.slice(start),
        start + insert.length, start + insert.length,
      ]);
    }
    return undefined;
  };

  const Tool = ({ title, onClick, children, style }) => (
    <button type="button" className="o-rte-btn" title={title} aria-label={title}
      disabled={disabled} style={style}
      // Keep the selection: a mousedown that moves focus out of the textarea is what makes the
      // first click of every button apply to nothing.
      onMouseDown={e => e.preventDefault()}
      onClick={onClick}>{children}</button>
  );

  return (
    <div className="o-rte" data-disabled={disabled ? 'true' : undefined}>
      <div className="o-rte-bar">
        {tab === 'write' && (
          <>
            {MARKS.map(m => (
              <Tool key={m.id} title={m.title} onClick={() => toggleWrap(m.wrap)}
                style={m.mono ? { fontFamily: 'var(--font-mono)', fontSize: '.6875rem' } : m.style}>
                {m.label}
              </Tool>
            ))}
            <span className="o-rte-sep" />
            {PREFIXES.map(p => (
              <Tool key={p.id} title={p.title} onClick={() => togglePrefix(p)}>{p.label}</Tool>
            ))}
            <span className="o-rte-sep" />
            <Tool title="Link  Ctrl+K" onClick={insertLink}>↗</Tool>
          </>
        )}
        <div className="o-toggle o-toggle-sm" style={{ marginLeft: 'auto' }}>
          <button type="button" aria-selected={tab === 'write'}
            onClick={() => setTab('write')}>Write</button>
          <button type="button" aria-selected={tab === 'preview'}
            onClick={() => setTab('preview')}>Preview</button>
        </div>
      </div>

      {tab === 'write' ? (
        <textarea
          ref={ta} id={fieldId} className="o-rte-input" value={value} placeholder={placeholder}
          disabled={disabled} onKeyDown={onKeyDown} onChange={e => onChange(e.target.value)}
          style={{ height: height || undefined, minHeight }}
        />
      ) : (
        <div className="o-rte-preview" style={{ minHeight }}>
          <Markdown text={value} empty="Nothing to preview yet." />
        </div>
      )}
    </div>
  );
}
